import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidenav';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import MyCardsContainer from 'pages/MyCardsContainer';
import ContactsContainer from 'pages/ContactsContainer';
import AccessoriesContainer from 'pages/AccessoriesContainer';
import InsightsContainer from 'pages/InsightsContainer';
import IntegrationsContainer from 'pages/IntegrationsContainer';
import SupportContainer from 'pages/SupportContainer';
import SettingsContainer from 'pages/SettingsContainer';
import EditCardContainer from 'pages/EditCardContainer';
import { useAuth } from 'contexts/AuthContext'; // Import useAuth
import Subscribe from 'pages/Subscribe';
import TagsContainer from 'pages/TagsContainer'; // Import TagsContainer

const Dashboard = () => {
  const { authState, isAuthenticated } = useAuth(); // Use authState instead of userState
  const location = useLocation();
  const navigate = useNavigate();

  // State to track the selected menu
  const [selectedMenu, setSelectedMenu] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication and redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/sign-in');
      return;
    }

    // Add a small delay to ensure all context updates are processed
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [authState.isAuthenticated, navigate]);



  // Effect to check the current URL and set the selected menu accordingly
  useEffect(() => {
    if (location.pathname.includes('/dashboard/subscribe')) {
      setSelectedMenu('subscribe');
    } else if (location.pathname.includes('/dashboard/settings')) {
      setSelectedMenu('settings');
    } else if (location.pathname.includes('/dashboard/mycards')) {
      setSelectedMenu('mycards');
    } else if (location.pathname.includes('/dashboard/contacts')) {
      setSelectedMenu('contacts');
    } else if (location.pathname.includes('/dashboard/tags')) {
      setSelectedMenu('tags');  // New Tags menu item
    } else if (location.pathname.includes('/dashboard/accessories')) {
      setSelectedMenu('accessories');
    } else if (location.pathname.includes('/dashboard/insights')) {
      setSelectedMenu('insights');
    } else if (location.pathname.includes('/dashboard/integrations')) {
      setSelectedMenu('integrations');
    } else if (location.pathname.includes('/dashboard/support')) {
      setSelectedMenu('support');
    } else {
      setSelectedMenu('');
    }
  }, [location.pathname]);

  // Menu click handler
  const onMenuClick = (menu) => {
    setSelectedMenu(menu); // Update the selected menu state

    switch (menu) {
      case 'mycards':
        navigate('/dashboard/mycards');
        break;
      case 'contacts':
        navigate('/dashboard/contacts');
        break;
      case 'tags':  // New case for Tags
        navigate('/dashboard/tags');
        break;
      case 'accessories':
        navigate('/dashboard/accessories');
        break;
      case 'insights':
        navigate('/dashboard/insights');
        break;
      case 'integrations':
        navigate('/dashboard/integrations');
        break;
      case 'support':
        navigate('/dashboard/support');
        break;
      case 'settings':
        navigate('/dashboard/settings');
        break;
      case 'subscribe':
        navigate('/dashboard/subscribe');
        break;
      default:
        navigate('/dashboard');
    }
  };

  // CRITICAL FIX: Add key to force re-mount of InsightsContainer when navigating back
  const getComponentKey = (path) => {
    // Create a unique key combining the path and a timestamp for certain components that need clean remounting
    if (path.includes('/insights')) {
      // Include userId to ensure clean remount when user changes
      const userIdPart = authState.user?.userId || 'guest';
      // Use a stable key to avoid unnecessary remounts, but add a dynamic part to force remount when navigating back
      return `insights-${userIdPart}-${Date.now()}`;
    }
    return path; // Default key for other components
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Sidebar onMenuClick={onMenuClick} selectedMenu={selectedMenu} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: '#f7f7f7',
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Routes>
            <Route path="mycards" element={<MyCardsContainer user={authState.user} />} />
            <Route path="contacts" element={<ContactsContainer user={authState.user} />} />
            <Route path="tags" element={<TagsContainer user={authState.user} />} />  {/* New Tags route */}
            <Route path="accessories" element={<AccessoriesContainer user={authState.user} />} />
            {/* CRITICAL FIX: Add key to force re-mount of InsightsContainer when navigating back */}
            <Route path="insights" element={<InsightsContainer key={getComponentKey(location.pathname)} user={authState.user} />} />
            <Route path="integrations" element={<IntegrationsContainer user={authState.user} />} />
            <Route path="support" element={<SupportContainer user={authState.user} />} />
            <Route path="settings" element={<SettingsContainer user={authState.user} />} />
            <Route path="mycards/edit/:cardId" element={<EditCardContainer user={authState.user} />} />
            <Route path="mycards/add-card/:cardId" element={<EditCardContainer user={authState.user} />} />
            <Route path="subscribe" element={<Subscribe user={authState.user} />} />

            {/* Fallback route */}
            <Route path="*" element={<MyCardsContainer user={authState.user} />} />
          </Routes>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

export default Dashboard;
